import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as pdfMake from 'pdfmake/build/pdfmake';

const CHINESE_FONT_PATH = '/assets/fonts/NotoSansTC-Regular.ttf';
const CHINESE_FONT_FILE = 'NotoSansTC-Regular.ttf';
const CHINESE_FONT_NAME = 'chinese';

@Injectable({ providedIn: 'root' })
export class PdfFontLoaderService {
    private chineseFontLoaded = false;
    private loadingPromise: Promise<void> | null = null;

    constructor(private translate: TranslateService) { }

    get isChinese(): boolean {
        const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'es';
        return lang === 'cht';
    }

    get fontName(): string {
        return this.isChinese ? CHINESE_FONT_NAME : 'Roboto';
    }

    async ensureFontsLoaded(): Promise<void> {
        if (!this.isChinese || this.chineseFontLoaded) return;

        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = this.loadChineseFont();
        try {
            await this.loadingPromise;
        } finally {
            this.loadingPromise = null;
        }
    }

    private async loadChineseFont(): Promise<void> {
        const response = await fetch(CHINESE_FONT_PATH);
        if (!response.ok) throw new Error('Failed to load Chinese font from assets');
        const buffer = await response.arrayBuffer();
        const base64 = this.arrayBufferToBase64(buffer);

        const pdfMakeAny = pdfMake as any;
        pdfMakeAny.addVirtualFileSystem({ [CHINESE_FONT_FILE]: base64 });
        pdfMakeAny.addFonts({
            [CHINESE_FONT_NAME]: {
                normal: CHINESE_FONT_FILE,
                bold: CHINESE_FONT_FILE,
                italics: CHINESE_FONT_FILE,
                bolditalics: CHINESE_FONT_FILE
            }
        });

        this.chineseFontLoaded = true;
    }

    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        const chunks: string[] = [];
        const chunkSize = 8192;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
        }
        return btoa(chunks.join(''));
    }
}
